'use client';
import { useMemo, useState } from 'react';
import { agreeAdjPlural } from '@/lib/hePluralAgree.mjs';
import Link from 'next/link';
import { wideImg } from '@/lib/imgUrl';

// Client body for the /מאמרים hub (2026-07-29, user request: search box at the top).
// Client component ONLY for the live filtering — it still server-renders fully on first
// load (grouped category sections, same markup as the previous pure-server version), so
// nothing changes for SEO/crawlers. Typing a query switches to a flat filtered grid.
export type HubArticle = {
  slug: string; url: string; title: string; desc: string | null;
  hero: string | null; catName: string; catSlug: string | null; catPlural: string | null;
};

// Loose Hebrew matching: ignore case (for Latin), collapse whitespace, and match every
// typed word independently so "מחיר פרגולה" finds "בניית פרגולה עץ — מחירים".
function matches(a: HubArticle, q: string): boolean {
  const hay = `${a.title} ${a.desc || ''} ${a.catName}`.toLowerCase();
  return q.toLowerCase().split(/\s+/).filter(Boolean).every(w => hay.includes(w));
}

function Card({ a }: { a: HubArticle }) {
  return (
    <Link href={a.url} className="rounded-2xl bg-white overflow-hidden block hover:shadow-lg transition-shadow" style={{ border: '1px solid #e2e8f0' }}>
      {a.hero && (
        // eslint-disable-next-line @next/next/no-img-element
        <img {...wideImg(a.hero, '(min-width: 768px) 360px, 62vw', 480, { kind: 'article', ctx: { title: a.title } })} loading="lazy" decoding="async" className="w-full h-40 object-cover" />
      )}
      <div className="p-4">
        <div className="font-bold leading-snug" style={{ color: '#0f172a' }}>{a.title}</div>
        {a.desc && <p className="text-sm mt-2 line-clamp-2" style={{ color: '#64748b' }}>{a.desc}</p>}
      </div>
    </Link>
  );
}

export default function ArticlesHubBody({ articles }: { articles: HubArticle[] }) {
  const [q, setQ] = useState('');
  const query = q.trim();

  const byCat = useMemo(() => {
    const m = new Map<string, HubArticle[]>();
    for (const a of articles) {
      if (!m.has(a.catName)) m.set(a.catName, []);
      m.get(a.catName)!.push(a);
    }
    return m;
  }, [articles]);

  const filtered = useMemo(() => (query ? articles.filter(a => matches(a, query)) : []), [articles, query]);

  return (
    <>
      {/* Search — filters across title/description/category, instant, no request */}
      <div className="mb-10" style={{ position: 'relative', maxWidth: 520 }}>
        <span style={{ position: 'absolute', insetInlineStart: 14, top: '50%', transform: 'translateY(-50%)', fontSize: 16, color: '#94a3b8', pointerEvents: 'none' }}>🔍</span>
        <input
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder="חיפוש מאמר... (למשל: מחיר פרגולה, החזר מס)"
          aria-label="חיפוש מאמרים"
          style={{
            width: '100%', boxSizing: 'border-box', padding: '13px 42px 13px 40px', fontSize: 15,
            borderRadius: 14, border: '1.5px solid #cbd5e1', background: '#fff', fontFamily: 'inherit',
            outline: 'none', boxShadow: '0 2px 10px rgba(15,23,42,.05)',
          }}
          onFocus={e => (e.currentTarget.style.borderColor = '#0d47a1')}
          onBlur={e => (e.currentTarget.style.borderColor = '#cbd5e1')}
        />
        {query && (
          <button onClick={() => setQ('')} aria-label="נקה חיפוש"
            style={{ position: 'absolute', insetInlineEnd: 10, top: '50%', transform: 'translateY(-50%)', width: 26, height: 26, borderRadius: '50%', border: 'none', background: '#f1f5f9', color: '#64748b', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
            ✕
          </button>
        )}
      </div>

      {query ? (
        <section className="mb-12">
          <h2 className="text-xl font-extrabold mb-4" style={{ color: '#0f172a' }}>
            {filtered.length ? `${filtered.length} מאמרים נמצאו` : 'לא נמצאו מאמרים תואמים'}
          </h2>
          {filtered.length === 0 && (
            <p style={{ color: '#64748b' }}>נסו מילה כללית יותר — למשל שם הקטגוריה (״מזגן״, ״משכנתא״, ״פרגולה״).</p>
          )}
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map(a => <Card key={a.slug} a={a} />)}
          </div>
        </section>
      ) : (
        [...byCat.entries()].map(([catName, items]) => (
          <section key={catName} id={items[0]?.catSlug || undefined} className="mb-12" style={{ scrollMarginTop: 20 }}>
            <div className="flex items-baseline justify-between mb-4">
              <h2 className="text-2xl font-extrabold" style={{ color: '#0f172a' }}>{catName}</h2>
              {items[0]?.catSlug && (
                <Link href={`/${items[0].catSlug}`} className="text-sm font-semibold hover:underline" style={{ color: '#1d4ed8' }}>
                  {items[0]?.catPlural || catName} {agreeAdjPlural(items[0]?.catPlural || catName, 'מומלצים')} ›
                </Link>
              )}
            </div>
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {items.map(a => <Card key={a.slug} a={a} />)}
            </div>
          </section>
        ))
      )}
    </>
  );
}
